export const mergeObjArrays = <A, B>(arr1: A[], arr2: B[]) => {
    return arr1.map((element, index) => {
        return { ...element, ...arr2[index] };
    });
};

export const getFirstElementOfArray = <T>(arr: T[]) => (arr.length === 0 ? null : arr[0]);

const isNotUndefined = <T>(value: T | undefined | null): value is T => {
    return value !== undefined && value !== null;
};

export const filterUndefinedOrNullValues = <T>(arr: (T | undefined | null)[]) => {
    return arr.filter(isNotUndefined);
};

/**
 * Deduplicates a list of objects using a specified unique key.
 * * It preserves the first occurrence of an object when a duplicate key value is found.
 *
 * @template T The type of the objects in the array.
 * @template K The type of the key (must be a property of T).
 * @param {T[]} list The array of objects to deduplicate.
 * @param {K} key The object property to use as the uniqueness key.
 * @returns {T[]} The deduplicated array of objects.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const deduplicateObjectsList = <T extends Record<string, any>, K extends keyof T>(
    list: T[],
    key: K
): T[] => {
    // Use a Map to store unique objects.
    // The Map's key is the value of the object's `key` property,
    // and the Map's value is the object itself (T).
    const uniqueMap = new Map<T[K], T>();

    // eslint-disable-next-line no-restricted-syntax
    for (const item of list) {
        const keyValue = item[key];

        // If the key is not already in the Map, store the object.
        // This ensures the FIRST occurrence is kept.
        if (!uniqueMap.has(keyValue)) {
            uniqueMap.set(keyValue, item);
        }
    }

    // Return the Map's values as an array
    return Array.from(uniqueMap.values());
};
